import React, {
  createContext, useContext, useMemo
} from 'react';
import PropTypes from 'prop-types';
import { useFetchSweepstakes, useUpdateSweepstakes } from '../hooks/fetch-sweepstakes';
import { isAuthenticated } from '../common/utils/AccountUtils';

const SweepstakesDataContext = createContext();

export const useSweepstakesData = () => {
  const context = useContext(SweepstakesDataContext);
  if (!context) {
    throw new Error('useSweepstakesData must be used within a SweepstakesDataProvider');
  }
  return context;
};

export const SweepstakesDataProvider = ({ children, sweepstakesData: providedData, userOptInStatus, useRestAPI }) => {
  // Fetch from API only if authenticated, pass providedData to override the response if provided
  const {
    challengeData, userProfileData, isLoading, hasError, refresh
  } = useFetchSweepstakes({
    providedData,
    userOptInStatus,
    skip: !isAuthenticated(),
    useRestAPI
  });

  // Use the update hook for managing runtime data changes
  const { effectiveData, updateSweepstakesData } = useUpdateSweepstakes(challengeData);

  const contextValue = useMemo(() => ({
    sweepstakesData: effectiveData,
    userProfileData,
    isLoading,
    hasError,
    refresh,
    updateSweepstakesData
  }), [effectiveData, userProfileData, isLoading, hasError, refresh, updateSweepstakesData]);

  return (
    <SweepstakesDataContext.Provider value={contextValue}>
      {children}
    </SweepstakesDataContext.Provider>
  );
};

SweepstakesDataProvider.propTypes = {
  children: PropTypes.node.isRequired,
  sweepstakesData: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    logo: PropTypes.string,
    status: PropTypes.string,
    expirationDate: PropTypes.string,
    isOptedIn: PropTypes.bool,
    completedSteps: PropTypes.number,
    steps: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      iconUrl: PropTypes.string,
      completed: PropTypes.bool,
    })),
  }),
  userOptInStatus: PropTypes.oneOf(['notOptedIn', 'partiallyCompleted', 'fullyCompleted']),
};

SweepstakesDataProvider.defaultProps = {
  sweepstakesData: null,
  userOptInStatus: 'notOptedIn',
};